import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';

type PastYearItem = {
  fiscalYear?: number;
  annualIncome?: number;
  monthlyAmount?: number;
  totalBackedInVariableIncomeFormula?: string | null;
  backingIntoVariableIncomeFormula?: string | null;
};

type W2ItemsYTDProps = {
  variableTitle?: string;
  w2Items?: {
    fiscalYear?: number;
    monthlyAmount?: number;
    annualIncome?: number;
  }[];
  pastYearItems?: PastYearItem[];
  showCalcEquation?: boolean;
};

export default function W2ItemsYTD({
  pastYearItems = [],
  w2Items = [],
  variableTitle = '',
  showCalcEquation = true,
}: Readonly<W2ItemsYTDProps>) {
  const showCalcs = !!pastYearItems.length && showCalcEquation;

  return (
    <>
      <Text
        textDecoration="underline"
        fontSize="md"
        fontWeight="bold"
        data-testid={'pay-type'}
      >
        W2
      </Text>
      <Flex alignItems={'flex-end'}>
        <Box data-testid="w2-item-headings">
          <Text mr={6} fontSize="md">
            W2 Total
          </Text>
          <Text mr={6} fontSize="md">
            Monthly
          </Text>
          {variableTitle && (
            <Text
              mr={6}
              fontSize="md"
              data-testid={`variable-title-${variableTitle}`}
            >
              {variableTitle}
            </Text>
          )}
        </Box>

        {!!pastYearItems.length &&
          pastYearItems.map(({ fiscalYear, annualIncome }, idx) => (
            <Box
              data-testid="past-year-section"
              key={`past-year-${fiscalYear}`}
              mr={6}
            >
              <Text data-testid={`fiscal-year-${idx}`} mr={1} fontWeight="bold">
                {fiscalYear}
              </Text>
              <Text data-testid={`w2-income-${idx}`}>
                {formatCurrency(w2Items[idx].annualIncome)}
              </Text>
              <Text data-testid={`monthly-amount-${idx}`} display="inline">
                {formatCurrency(w2Items[idx].monthlyAmount)}
              </Text>
              <Text data-testid={`annual-income-${idx}`}>
                {formatCurrency(annualIncome)}
              </Text>
            </Box>
          ))}
      </Flex>

      {showCalcs && (
        <Box my={5}>
          {pastYearItems.map(
            ({ fiscalYear, totalBackedInVariableIncomeFormula }) => (
              <Text
                key={`${fiscalYear}-totalbackedintoformula}`}
                data-testid="totalBackedIntoFormula"
              >
                {fiscalYear} Variable Income:&nbsp;
                {totalBackedInVariableIncomeFormula}
              </Text>
            ),
          )}
        </Box>
      )}

      {showCalcs && (
        <Box my={5}>
          {pastYearItems.map(
            ({ fiscalYear, backingIntoVariableIncomeFormula }) => (
              <Text
                key={`${fiscalYear}-backingintoformula}`}
                data-testid="backingIntoFormula"
              >
                {fiscalYear} {variableTitle.trim()}:&nbsp;
                {backingIntoVariableIncomeFormula}
              </Text>
            ),
          )}
        </Box>
      )}
    </>
  );
}
